import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HiOutlineViewGrid, HiOutlineFolder, HiOutlineCog,
  HiOutlineClipboardList,
  HiOutlinePlay,
  HiOutlineViewGrid as HiOutlineMatrix, HiOutlineChartBar,
  HiChevronLeft, HiChevronRight,
  HiOutlineUserGroup, HiOutlineLogout,
  HiOutlineMoon, HiOutlineSun,
  HiOutlineUser,
} from 'react-icons/hi';
import { FiActivity, FiRefreshCw, FiTarget, FiAlertOctagon, FiFileText, FiServer, FiPaperclip } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

import NotificationBell from './NotificationBell';
import { getAvatarUrl } from '../services/auth';

export default function Sidebar({ projects, currentProject, sidebarOpen, onToggleSidebar }) {
  const location = useLocation();
  const projectMatch = /^\/projects\/(\d+)/.exec(location.pathname);
  const projectId = projectMatch ? projectMatch[1] : null;
  const navigate = useNavigate();
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const isActive = (path) => location.pathname === path;

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    }
    return (user?.username || '?').slice(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const userBtnRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ bottom: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        userMenuOpen &&
        userMenuRef.current && !userMenuRef.current.contains(e.target) &&
        userBtnRef.current && !userBtnRef.current.contains(e.target)
      ) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const handleUserMenuToggle = () => {
    const willOpen = !userMenuOpen;
    if (willOpen && userBtnRef.current) {
      const rect = userBtnRef.current.getBoundingClientRect();
      setMenuPos({
        bottom: window.innerHeight - rect.top + 6,
        left: rect.left,
      });
    }
    setUserMenuOpen(willOpen);
  };

  return (
    <div className="sidebar-wrapper">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Brand */}
        <div className="sidebar-logo">
          <Link to="/" className="sidebar-brand-link text-decoration-none" aria-label="Alton">
            {/* Logo Alton seul : variante inversee (point vert) sur fond sombre */}
            <img
              src={theme === 'dark' ? '/alton-logo-inverted.svg' : '/alton-logo.svg'}
              alt="Alton"
              className="sidebar-alton-logo"
            />
            {/* Marque compacte, visible uniquement quand la sidebar est reduite */}
            <span className="sidebar-mark" aria-hidden="true">
              A<span className="sidebar-mark-dot">.</span>
            </span>
          </Link>
          <div className="sidebar-header-actions">
            <button className="sidebar-theme-btn" onClick={toggleTheme} title={theme === 'light' ? 'Mode sombre' : 'Mode clair'}>
              {theme === 'light' ? <HiOutlineMoon /> : <HiOutlineSun />}
            </button>
            <NotificationBell />
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="sidebar-section-label">Menu</div>

          <Link to="/" className={`sidebar-link ${isActive('/') ? 'active' : ''}`} data-tooltip="Dashboard">
            <HiOutlineViewGrid className="sidebar-icon" />
            <span className="sidebar-link-text">Dashboard</span>
          </Link>

          <Link to="/projects" className={`sidebar-link ${isActive('/projects') || projectId ? 'active' : ''}`} data-tooltip="Projets">
            <HiOutlineFolder className="sidebar-icon" />
            <span className="sidebar-link-text">Projets</span>
            {projects && <span className="sidebar-badge">{projects.length}</span>}
          </Link>

          {/* Project Sub-menu — indente sous Projets */}
          {projectId && currentProject && (
            <>
              <div className="sidebar-section-label mt-2" style={{ paddingLeft: '1.25rem' }}>{currentProject.name}</div>

              <div className="sidebar-submenu">
                <Link to={`/projects/${projectId}`}
                  className={`sidebar-sublink ${isActive(`/projects/${projectId}`) ? 'active' : ''}`}>
                  <HiOutlineClipboardList className="sidebar-icon" />
                  Exigences & Tests
                </Link>
                <Link to={`/projects/${projectId}/campaigns`}
                  className={`sidebar-sublink ${location.pathname.startsWith(`/projects/${projectId}/campaigns`) ? 'active' : ''}`}>
                  <FiTarget className="sidebar-icon" />
                  Campagnes
                </Link>
                <Link to={`/projects/${projectId}/defects`}
                  className={`sidebar-sublink ${isActive(`/projects/${projectId}/defects`) ? 'active' : ''}`}>
                  <FiAlertOctagon className="sidebar-icon" />
                  Defauts
                </Link>
                <Link to={`/projects/${projectId}/execution`}
                  className={`sidebar-sublink ${isActive(`/projects/${projectId}/execution`) ? 'active' : ''}`}>
                  <HiOutlinePlay className="sidebar-icon" />
                  Execution des Tests
                </Link>
                <Link to={`/projects/${projectId}/traceability`}
                  className={`sidebar-sublink ${isActive(`/projects/${projectId}/traceability`) ? 'active' : ''}`}>
                  <HiOutlineMatrix className="sidebar-icon" />
                  Matrice de Tracabilite
                </Link>
                <Link to={`/projects/${projectId}/reports`}
                  className={`sidebar-sublink ${isActive(`/projects/${projectId}/reports`) ? 'active' : ''}`}>
                  <HiOutlineChartBar className="sidebar-icon" />
                  Rapports
                </Link>
              </div>
            </>
          )}

          <Link to="/squash-tracking" className={`sidebar-link ${isActive('/squash-tracking') ? 'active' : ''}`} data-tooltip="Historique Sync">
            <FiRefreshCw className="sidebar-icon" />
            <span className="sidebar-link-text">Historique Sync Squash</span>
          </Link>

          {/* Configuration (admin only) */}
          {isAdmin && (
            <>
              <div className="sidebar-section-label mt-3">Systeme</div>
              <Link to="/configuration" className={`sidebar-link ${isActive('/configuration') ? 'active' : ''}`} data-tooltip="Configuration">
                <HiOutlineCog className="sidebar-icon" />
                <span className="sidebar-link-text">Configuration</span>
              </Link>
              <Link to="/monitoring" className={`sidebar-link ${isActive('/monitoring') ? 'active' : ''}`} data-tooltip="Monitoring">
                <FiActivity className="sidebar-icon" />
                <span className="sidebar-link-text">Monitoring</span>
              </Link>
              <Link to="/users" className={`sidebar-link ${isActive('/users') ? 'active' : ''}`} data-tooltip="Utilisateurs">
                <HiOutlineUserGroup className="sidebar-icon" />
                <span className="sidebar-link-text">Utilisateurs</span>
              </Link>
              <Link to="/admin/audit" className={`sidebar-link ${isActive('/admin/audit') ? 'active' : ''}`} data-tooltip="Journalisation & Audit">
                <FiFileText className="sidebar-icon" />
                <span className="sidebar-link-text">Journalisation & Audit</span>
              </Link>
              <Link to="/admin/system-logs" className={`sidebar-link ${isActive('/admin/system-logs') ? 'active' : ''}`} data-tooltip="Logs Systeme">
                <FiServer className="sidebar-icon" />
                <span className="sidebar-link-text">Logs Systeme</span>
              </Link>
              {/* Documents sources : reserve au superadmin */}
              {isSuperAdmin && (
                <Link to="/admin/ai-sources" className={`sidebar-link ${isActive('/admin/ai-sources') ? 'active' : ''}`} data-tooltip="Documents sources IA">
                  <FiPaperclip className="sidebar-icon" />
                  <span className="sidebar-link-text">Documents sources IA</span>
                </Link>
              )}
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {user && (
            <div className="sidebar-user-section">
              <button
                type="button"
                ref={userBtnRef}
                className="sidebar-user-clickable"
                onClick={handleUserMenuToggle}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', flex: 1, minWidth: 0, background: 'none', border: 'none', font: 'inherit', color: 'inherit' }}
              >
                {user.avatar ? (
                  <img src={getAvatarUrl(user.avatar)} alt="" className="sidebar-user-avatar-img" />
                ) : (
                  <div className="sidebar-user-avatar">{getInitials()}</div>
                )}
                <div className="sidebar-user-info">
                  <span className="sidebar-user-name">{user.full_name || user.username}</span>
                  <span className="sidebar-user-role">{{ superadmin: 'Super admin', admin: 'Admin' }[user.role] || 'Utilisateur'}</span>
                </div>
              </button>
              {userMenuOpen && (
                <div
                  ref={userMenuRef}
                  className="sidebar-user-menu"
                  style={{
                    position: 'fixed',
                    bottom: menuPos.bottom,
                    left: menuPos.left,
                  }}
                >
                  <Link to="/profile" className="sidebar-user-menu-item" onClick={() => setUserMenuOpen(false)}>
                    <HiOutlineUser /> Profil
                  </Link>
                  {isAdmin && (
                    <Link to="/configuration" className="sidebar-user-menu-item" onClick={() => setUserMenuOpen(false)}>
                      <HiOutlineCog /> Configuration
                    </Link>
                  )}
                  <div className="sidebar-user-menu-divider"></div>
                  <button className="sidebar-user-menu-item sidebar-user-menu-logout" onClick={handleLogout}>
                    <HiOutlineLogout /> Deconnexion
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="sidebar-version">v1.0.0</div>
        </div>
      </aside>

      {/* Toggle arrow on edge - always visible */}
      <button
        className={`sidebar-edge-toggle ${sidebarOpen ? '' : 'collapsed'}`}
        onClick={onToggleSidebar}
        title={sidebarOpen ? 'Reduire' : 'Afficher'}
      >
        {sidebarOpen ? <HiChevronLeft /> : <HiChevronRight />}
      </button>
    </div>
  );
}
